/*!
 * ETAR theme v4.2.4 — Collection page "The scent shelf" (filters, price slider, sorting, load more)
 * Developed by FACTICIED · https://www.facticied.com
 */
(() => {
  if (customElements.get('et-collection')) return;

  class EtCollection extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const opt = { signal: this.ac.signal };
      this.sid = this.dataset.section;
      this.drawer = this.querySelector('[data-filters]');
      this.form = this.querySelector('[data-filter-form]');
      this.sort = this.querySelector('[data-sort]');

      // Drawer
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-filter-open]')) { this.openDrawer(); return; }
        if (e.target.closest('[data-filter-close]')) { this.closeDrawer(); return; }
        const link = e.target.closest('a[data-filter-link]');
        if (link) { e.preventDefault(); this.go(new URL(link.href, location.href).searchParams); return; }
        const more = e.target.closest('a[data-more]');
        if (more) { e.preventDefault(); this.more(more); return; }
        const v = e.target.closest('[data-cols]');
        if (v && v.tagName === 'BUTTON') this.setCols(v.dataset.cols, true);
      }, opt);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.closeDrawer(); }, opt);

      // Filters
      if (this.form) {
        this.form.addEventListener('change', (e) => { if (e.target.type !== 'number') this.queue(0); }, opt);
        this.form.addEventListener('input', (e) => {
          const t = e.target;
          if (t.matches('[data-range-min], [data-range-max]')) { this.fromSlider(t); this.queue(700); return; }
          if (t.type === 'number') { this.fromFields(t); this.queue(650); }
        }, opt);
        this.paintRanges();
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this.queue(0); this.closeDrawer(); }, opt);
      }
      this.sort?.addEventListener('change', (e) => { e.stopPropagation(); this.queue(0); }, opt);

      window.addEventListener('popstate', () => this.go(new URLSearchParams(location.search), false), opt);

      // Visitor's grid choice
      let saved = null;
      try { saved = localStorage.getItem('et-cl-cols'); } catch (e) { /* storage unavailable */ }
      if (saved && window.matchMedia('(min-width: 990px)').matches) this.setCols(saved, false);
      else this.markCols();
    }

    disconnectedCallback() { this.ac?.abort(); this.req?.abort(); clearTimeout(this.t); this.untrap?.(); }

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.querySelector('[data-filter-open]')?.setAttribute('aria-expanded', 'true');
      if (getComputedStyle(this.drawer).position === 'fixed') {
        window.Etar?.lockScroll(true);
        this.untrap = window.Etar?.trapFocus(this.drawer);
        setTimeout(() => this.drawer.querySelector('.et-cl__x')?.focus(), 80);
      }
    }

    closeDrawer() {
      if (!this.drawer?.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.untrap?.(); this.untrap = null;
      window.Etar?.lockScroll(false);
      const btn = this.querySelector('[data-filter-open]');
      btn?.setAttribute('aria-expanded', 'false');
      btn?.focus();
    }

    params() {
      const p = new URLSearchParams();
      if (this.form) {
        for (const [k, v] of new FormData(this.form)) { if (String(v).trim() !== '') p.append(k, v); }
      } else {
        new URLSearchParams(location.search).forEach((v, k) => { if (k !== 'page' && k !== 'sort_by') p.append(k, v); });
      }
      if (this.sort) p.set('sort_by', this.sort.value);
      return p;
    }

    /* two-handle price slider, kept in step with the two number fields */
    fromSlider(t) {
      const box = t.closest('[data-price-range]');
      const a = box.querySelector('[data-range-min]'), b = box.querySelector('[data-range-max]');
      if (+a.value > +b.value) { if (t === a) a.value = b.value; else b.value = a.value; }
      const max = +box.dataset.max || +b.max;
      box.querySelector('[data-num-min]').value = +a.value > 0 ? a.value : '';
      box.querySelector('[data-num-max]').value = +b.value < max ? b.value : '';
      this.paintRanges(box);
    }
    fromFields(t) {
      const box = t.closest('[data-price-range]');
      if (!box) return;
      const max = +box.dataset.max || 0;
      box.querySelector('[data-range-min]').value = box.querySelector('[data-num-min]').value || 0;
      box.querySelector('[data-range-max]').value = box.querySelector('[data-num-max]').value || max;
      this.paintRanges(box);
    }
    paintRanges(only) {
      (only ? [only] : this.querySelectorAll('[data-price-range]')).forEach((box) => {
        const max = +box.dataset.max || 1;
        const a = +box.querySelector('[data-range-min]').value, b = +box.querySelector('[data-range-max]').value;
        const fill = box.querySelector('[data-range-fill]');
        fill.style.setProperty('--a', (a / max) * 100 + '%');
        fill.style.setProperty('--b', (b / max) * 100 + '%');
      });
    }

    queue(ms) { clearTimeout(this.t); this.t = setTimeout(() => this.go(this.params()), ms); }

    async fetchSection(params) {
      this.req?.abort();
      this.req = new AbortController();
      const q = new URLSearchParams(params);
      q.set('section_id', this.sid);
      const res = await fetch(`${location.pathname}?${q}`, { signal: this.req.signal });
      if (!res.ok) throw new Error(res.status);
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    }

    async go(params, push = true) {
      params.delete('page');
      this.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(params);
        const fresh = doc.querySelector('et-collection');
        if (!fresh) throw new Error('no section');
        this.swap(fresh);
        const qs = params.toString();
        if (push) history.pushState({}, '', `${location.pathname}${qs ? '?' + qs : ''}`);
      } catch (e) {
        if (e.name !== 'AbortError') { const qs = params.toString(); location.href = `${location.pathname}${qs ? '?' + qs : ''}`; }
      } finally { this.classList.remove('is-loading'); }
    }

    swap(fresh) {
      // Results + chips
      const main = this.querySelector('.et-cl__main'), fMain = fresh.querySelector('.et-cl__main');
      if (main && fMain) main.innerHTML = fMain.innerHTML;
      // Count + badge
      const c = this.querySelector('[data-count]'), fc = fresh.querySelector('[data-count]');
      if (c && fc) c.textContent = fc.textContent;
      const b = this.querySelector('[data-filter-open]'), fb = fresh.querySelector('[data-filter-open]');
      if (b && fb) b.innerHTML = fb.innerHTML;
      // Filter groups: keep what the visitor has open and where focus was
      const form = this.form, fForm = fresh.querySelector('[data-filter-form]');
      if (form && fForm) {
        const openNow = Array.from(form.querySelectorAll('details')).map((d) => d.open);
        const scroll = form.scrollTop, panelScroll = this.drawer?.scrollTop || 0;
        const active = document.activeElement && form.contains(document.activeElement) ? document.activeElement.name + '|' + document.activeElement.value : null;
        form.innerHTML = fForm.innerHTML;
        form.querySelectorAll('details').forEach((d, i) => { if (openNow[i] !== undefined) d.open = openNow[i]; });
        form.scrollTop = scroll;
        if (this.drawer) this.drawer.scrollTop = panelScroll;
        if (active) {
          const [n, v] = active.split('|');
          const el = Array.from(form.elements).find((x) => x.name === n && (x.type !== 'checkbox' || x.value === v));
          if (el && el.type !== 'number') el.focus({ preventScroll: true });
        }
      }
      this.markCols();
      this.paintRanges();
      /* family chips follow the filters */
      const fam = this.querySelector('.et-cl__fam'), fFam = fresh.querySelector('.et-cl__fam');
      if (fam && fFam) fam.innerHTML = fFam.innerHTML;
    }

    async more(a) {
      a.classList.add('is-busy');
      const old = a.textContent;
      a.textContent = a.dataset.loading || '…';
      try {
        const url = new URL(a.href, location.href);
        const doc = await this.fetchSection(url.searchParams);
        const grid = this.querySelector('[data-grid]'), fGrid = doc.querySelector('[data-grid]');
        const wrap = this.querySelector('.et-cl__more'), fWrap = doc.querySelector('.et-cl__more');
        if (grid && fGrid) {
          Array.from(fGrid.children).forEach((li, i) => { li.style.setProperty('--i', i); grid.appendChild(li); });
        }
        if (wrap && fWrap) wrap.replaceWith(fWrap);
        history.replaceState({}, '', url.pathname + url.search);
      } catch (e) {
        if (e.name !== 'AbortError') location.href = a.href;
      } finally { a.classList.remove('is-busy'); a.textContent = old; }
    }

    setCols(n, save) {
      const pg = this.querySelector('.et-pg');
      if (pg) pg.dataset.cols = n;
      this.cols = n;
      if (save) { try { localStorage.setItem('et-cl-cols', n); } catch (e) { /* storage unavailable */ } }
      this.markCols();
    }

    markCols() {
      const pg = this.querySelector('.et-pg');
      if (pg && this.cols) pg.dataset.cols = this.cols;
      const cur = this.cols || getComputedStyle(pg || this).getPropertyValue('--pg-cols').trim();
      this.querySelectorAll('.et-cl__view [data-cols]').forEach((b) => {
        const on = b.dataset.cols === String(cur);
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  }

  customElements.define('et-collection', EtCollection);
})();
